import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Trash2, Eye } from 'lucide-react';
import { ApprovalBadge, RowActions, PendingApprovalBanner, PendingCountBadge } from '../components/ApprovalActions';
import { formatCurrency, formatDate, today } from '../utils/helpers';

const emptyLine = { account_id:'', debit:0, credit:0, narration:'' };
const emptyForm = { entry_date:today(), narration:'', lines:[{...emptyLine},{...emptyLine}] };

export default function Journal() {
  const [list, setList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [viewId, setViewId] = useState(null);
  const [viewData, setViewData] = useState(null);

  const load = () => api.get('/journal').then(r => setList(r.data.data || []));
  useEffect(() => { load(); api.get('/accounts').then(r => setAccounts(r.data.data || [])); }, []);

  const addLine = () => setForm({...form, lines:[...form.lines, {...emptyLine}]});
  const removeLine = (i) => setForm({...form, lines: form.lines.filter((_,ix)=>ix!==i)});
  const updateLine = (i, field, val) => {
    const lines = [...form.lines];
    lines[i] = {...lines[i], [field]:val};
    setForm({...form, lines});
  };

  const totalDebit = form.lines.reduce((s,l) => s + parseFloat(l.debit||0), 0);
  const totalCredit = form.lines.reduce((s,l) => s + parseFloat(l.credit||0), 0);

  const save = async () => {
    if (Math.abs(totalDebit - totalCredit) > 0.01) return toast.error('Debit must equal Credit');
    if (totalDebit <= 0) return toast.error('Enter amounts');
    try { await api.post('/journal', form); toast.success('Journal entry created'); setOpen(false); setForm(emptyForm); load(); }
    catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const view = async (id) => {
    const r = await api.get('/journal/' + id);
    setViewData(r.data.data); setViewId(id);
  };

  const pendingCount = list.filter(x => x.approval_status === 'pending').length;
  return (
    <div className="card">
      <div className="card-header">
        <h3>Journal Entries ({list.length})<PendingCountBadge count={pendingCount}/></h3>
        <button className="btn btn-blue" onClick={() => { setForm(emptyForm); setOpen(true); }}><Plus size={14}/> New Entry</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Entry No</th><th>Date</th><th>Narration</th><th className="td-num">Debit</th><th className="td-num">Credit</th><th>By</th><th>Approval</th><th>Actions</th></tr></thead>
          <tbody>
            {list.length ? list.map(j => (
              <tr key={j.id} style={j.approval_status==='pending'?{background:'#fffbeb'}:{}}>
                <td><strong>{j.entry_no}</strong></td>
                <td>{formatDate(j.entry_date)}</td>
                <td>{j.narration}</td>
                <td className="td-num">{formatCurrency(j.total_debit)}</td>
                <td className="td-num">{formatCurrency(j.total_credit)}</td>
                <td>{j.created_by_name}</td>
                <td><ApprovalBadge status={j.approval_status} createdByName={j.created_by_name}/></td>
                <td>
                  <button className="btn btn-sm btn-gray" onClick={()=>view(j.id)}><Eye size={12}/></button>{' '}
                  <RowActions endpoint="/journal" row={j} onChange={load}/>
                </td>
              </tr>
            )) : <tr><td colSpan="8" className="empty">No journal entries</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title="New Journal Entry" size="lg"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>Save</button>
        </>}>
        <div className="form-grid">
          <div className="form-group"><label>Date *</label><input type="date" value={form.entry_date} onChange={e=>setForm({...form, entry_date:e.target.value})}/></div>
          <div className="form-group full-width"><label>Narration</label><input value={form.narration} onChange={e=>setForm({...form, narration:e.target.value})}/></div>
        </div>

        <h4 style={{margin:'16px 0 8px',fontSize:13}}>Lines</h4>
        <table className="items-table">
          <thead><tr><th>Account</th><th>Narration</th><th>Debit</th><th>Credit</th><th></th></tr></thead>
          <tbody>
            {form.lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <select value={l.account_id} onChange={e=>updateLine(i, 'account_id', e.target.value)}>
                    <option value="">-- Select --</option>
                    {accounts.map(a=><option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
                  </select>
                </td>
                <td><input value={l.narration} onChange={e=>updateLine(i, 'narration', e.target.value)}/></td>
                <td><input type="number" step="0.01" value={l.debit} onChange={e=>updateLine(i, 'debit', e.target.value)}/></td>
                <td><input type="number" step="0.01" value={l.credit} onChange={e=>updateLine(i, 'credit', e.target.value)}/></td>
                <td><button className="btn-icon" onClick={()=>removeLine(i)}><Trash2 size={12}/></button></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{background:'#f1f5f9',fontWeight:600}}>
              <td colSpan="2" style={{textAlign:'right',padding:8}}>TOTALS</td>
              <td style={{padding:8}}>{formatCurrency(totalDebit)}</td>
              <td style={{padding:8}}>{formatCurrency(totalCredit)}</td>
              <td style={{padding:8,color:Math.abs(totalDebit-totalCredit)<0.01?'#059669':'#dc2626'}}>{Math.abs(totalDebit-totalCredit)<0.01?'✓':'✗'}</td>
            </tr>
          </tfoot>
        </table>
        <button type="button" className="btn btn-sm btn-gray" style={{marginTop:8}} onClick={addLine}><Plus size={12}/> Add Line</button>
      </Modal>

      <Modal open={!!viewId} onClose={()=>{setViewId(null);setViewData(null);}} title={viewData?.entry_no} size="lg">
        {viewData && <>
          <div style={{marginBottom:14}}>
            <p><strong>Date:</strong> {formatDate(viewData.entry_date)}</p>
            <p><strong>Narration:</strong> {viewData.narration}</p>
          </div>
          <table className="items-table">
            <thead><tr><th>Account</th><th>Narration</th><th>Debit</th><th>Credit</th></tr></thead>
            <tbody>
              {viewData.lines?.map(l => (
                <tr key={l.id} style={l.approval_status==='pending'?{background:'#fffbeb'}:{}}>
                  <td>{l.account_code} — {l.account_name}</td>
                  <td>{l.narration||'-'}</td>
                  <td>{formatCurrency(l.debit)}</td>
                  <td>{formatCurrency(l.credit)}</td>
                </tr>
              ))}
              <tr style={{background:'#f1f5f9',fontWeight:600}}>
                <td colSpan="2" style={{textAlign:'right'}}>TOTAL</td>
                <td>{formatCurrency(viewData.total_debit)}</td>
                <td>{formatCurrency(viewData.total_credit)}</td>
              </tr>
            </tbody>
          </table>
        </>}
      </Modal>
    </div>
  );
}
